import { useEffect, useState, useCallback } from "react";
import { Link, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { LayoutDashboard, Package, ShoppingCart, Users, LogOut, Plus, Edit2, Trash2 } from "lucide-react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const LOGO_URL = "/logo.png";
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/signin?redirect=/admin"); return; }
    if (!user.is_admin) { toast.error("Admin access required"); navigate("/"); }
  }, [user, loading, navigate]);

  if (!user || !user.is_admin) return null;

  const menu = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
    { to: "/admin/products", label: "Products", icon: Package },
    { to: "/admin/orders", label: "Orders", icon: ShoppingCart },
    { to: "/admin/users", label: "Users", icon: Users },
  ];

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex" data-testid="admin-page">
      <aside className="w-64 bg-[#0A192F] text-white flex-shrink-0 flex flex-col">
        <div className="p-6 border-b border-white/10">
          <div className="inline-block bg-[#F9F6F0] rounded-lg px-3 py-2">
            <img src={LOGO_URL} alt="Roop Anand" className="h-7 w-auto" />
          </div>
          <p className="text-xs uppercase tracking-widest text-[#E65A28] font-semibold mt-3">Admin Panel</p>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {menu.map((m) => {
            const active = m.exact ? location.pathname === m.to : location.pathname.startsWith(m.to);
            return (
              <Link
                key={m.to}
                to={m.to}
                className={`flex items-center gap-3 px-3 py-2 rounded text-sm ${active ? "bg-[#E65A28] text-white" : "hover:bg-white/5 text-white/80"}`}
                data-testid={`admin-nav-${m.label.toLowerCase()}`}
              >
                <m.icon className="h-4 w-4" /> {m.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/10 space-y-2">
          <Link to="/" className="block text-xs text-white/60 hover:text-white">← Back to store</Link>
          <button onClick={logout} className="flex items-center gap-2 text-xs text-white/60 hover:text-white" data-testid="admin-logout"><LogOut className="h-3 w-3" /> Sign out</button>
        </div>
      </aside>
      <main className="flex-1 p-8 overflow-auto">
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="products" element={<ProductsAdmin />} />
          <Route path="orders" element={<OrdersAdmin />} />
          <Route path="users" element={<UsersAdmin />} />
        </Routes>
      </main>
    </div>
  );
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(r => setStats(r.data)); }, []);
  if (!stats) return <p className="text-sm text-gray-500">Loading...</p>;
  const cards = [
    { label: "Revenue", value: `₹${stats.revenue.toFixed(0)}`, tint: "#E65A28" },
    { label: "Orders", value: stats.total_orders, tint: "#0A192F" },
    { label: "Pending Orders", value: stats.pending_orders, tint: "#E65A28" },
    { label: "Products", value: stats.total_products, tint: "#0A192F" },
    { label: "Users", value: stats.total_users, tint: "#0A192F" },
  ];
  return (
    <div>
      <h1 className="text-2xl font-semibold mb-6">Dashboard</h1>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-white border border-gray-200 rounded-md p-5" data-testid={`stat-${c.label.toLowerCase().replace(/\s+/g,"-")}`}>
            <p className="text-xs uppercase tracking-wider text-gray-500">{c.label}</p>
            <p className="text-2xl font-semibold mt-2" style={{ color: c.tint }}>{c.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProductsAdmin() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => api.get("/products").then((r) => setProducts(r.data)), []);
  useEffect(() => {
    load();
    api.get("/categories").then(r => setCats(r.data));
  }, [load]);

  const empty = { name:"", slug:"", short_description:"", description:"", price:0, compare_at_price:0, category_slug:"hair-care", image_url:"", stock:100, is_featured:false, is_active:true, ingredients:"", how_to_use:"", benefits: [] };

  const openNew = () => { setEditing({ ...empty }); setOpen(true); };
  const openEdit = (p) => { setEditing({ ...p, benefits: p.benefits || [] }); setOpen(true); };

  const save = async () => {
    if (!editing.name || !editing.slug || !editing.image_url) { toast.error("Name, slug and image_url required"); return; }
    try {
      if (editing.product_id) {
        await api.put(`/admin/products/${editing.product_id}`, editing);
        toast.success("Updated");
      } else {
        await api.post("/admin/products", editing);
        toast.success("Created");
      }
      setOpen(false); load();
    } catch (e) { toast.error("Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Products</h1>
        <button onClick={openNew} className="inline-flex items-center gap-2 bg-[#E65A28] text-white px-4 py-2 rounded-md text-sm" data-testid="admin-new-product-btn"><Plus className="h-4 w-4" /> New Product</button>
      </div>

      <div className="bg-white border border-gray-200 rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Image</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Stock</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map((p) => (
              <TableRow key={p.product_id} data-testid={`admin-product-row-${p.slug}`}>
                <TableCell><img src={abs(p.image_url)} alt="" className="h-10 w-10 rounded object-contain bg-gray-50" /></TableCell>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell className="text-xs">{p.category_slug}</TableCell>
                <TableCell>₹{p.price}</TableCell>
                <TableCell>{p.stock}</TableCell>
                <TableCell>{p.is_active ? "Yes" : "No"}</TableCell>
                <TableCell className="text-right">
                  <button onClick={() => openEdit(p)} className="p-1 text-gray-600 hover:text-[#E65A28]" data-testid={`edit-${p.slug}`}><Edit2 className="h-4 w-4" /></button>
                  <button onClick={() => del(p.product_id)} className="p-1 text-gray-600 hover:text-red-600 ml-2" data-testid={`delete-${p.slug}`}><Trash2 className="h-4 w-4" /></button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.product_id ? "Edit Product" : "New Product"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Name</Label><Input value={editing.name} onChange={(e) => setEditing({...editing, name: e.target.value})} data-testid="admin-product-name" /></div>
                <div><Label>Slug</Label><Input value={editing.slug} onChange={(e) => setEditing({...editing, slug: e.target.value})} /></div>
                <div><Label>Category</Label>
                  <Select value={editing.category_slug} onValueChange={(v) => setEditing({...editing, category_slug: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{cats.map(c => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Price (₹)</Label><Input type="number" value={editing.price} onChange={(e) => setEditing({...editing, price: Number(e.target.value)})} /></div>
                <div><Label>Compare price (₹)</Label><Input type="number" value={editing.compare_at_price || 0} onChange={(e) => setEditing({...editing, compare_at_price: Number(e.target.value)})} /></div>
                <div><Label>Stock</Label><Input type="number" value={editing.stock} onChange={(e) => setEditing({...editing, stock: Number(e.target.value)})} /></div>
                <div className="flex items-end gap-4">
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.is_featured} onChange={(e) => setEditing({...editing, is_featured: e.target.checked})} /> Featured</label>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({...editing, is_active: e.target.checked})} /> Active</label>
                </div>
              </div>

              <ImageUploader
                mainImage={editing.image_url}
                gallery={editing.gallery || []}
                onMainChange={(url) => setEditing({...editing, image_url: url})}
                onGalleryChange={(g) => setEditing({...editing, gallery: g})}
              />

              <BenefitHighlightsEditor
                value={editing.benefit_highlights || []}
                onChange={(bh) => setEditing({...editing, benefit_highlights: bh})}
              />
              <div><Label>Short description</Label><Input value={editing.short_description} onChange={(e) => setEditing({...editing, short_description: e.target.value})} /></div>
              <div><Label>Description</Label><Textarea rows={3} value={editing.description} onChange={(e) => setEditing({...editing, description: e.target.value})} /></div>
              <div><Label>Ingredients</Label><Textarea rows={2} value={editing.ingredients} onChange={(e) => setEditing({...editing, ingredients: e.target.value})} /></div>
              <div><Label>How to use</Label><Textarea rows={2} value={editing.how_to_use} onChange={(e) => setEditing({...editing, how_to_use: e.target.value})} /></div>
              <div className="flex justify-end gap-3">
                <button onClick={() => setOpen(false)} className="border border-gray-300 px-5 py-2 rounded-md text-sm">Cancel</button>
                <button onClick={save} className="bg-[#E65A28] text-white px-5 py-2 rounded-md text-sm" data-testid="admin-save-product">Save</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OrdersAdmin() {
  const [orders, setOrders] = useState([]);
  const load = useCallback(() => api.get("/admin/orders").then((r) => setOrders(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (order_id, status) => {
    await api.put(`/admin/orders/${order_id}/status`, { order_status: status });
    toast.success("Status updated"); load();
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-6">Orders</h1>
      <div className="bg-white border border-gray-200 rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Items</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Payment</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.map((o) => (
              <TableRow key={o.order_id}>
                <TableCell className="font-mono text-xs">{o.order_id}</TableCell>
                <TableCell className="text-xs">{new Date(o.created_at).toLocaleDateString()}</TableCell>
                <TableCell>{o.items.length}</TableCell>
                <TableCell>₹{o.total.toFixed(0)}</TableCell>
                <TableCell className="text-xs">{o.payment_method} • {o.payment_status}</TableCell>
                <TableCell>
                  <Select value={o.order_status} onValueChange={(v) => updateStatus(o.order_id, v)}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["confirmed","packed","shipped","out_for_delivery","delivered","cancelled"].map(s => (
                        <SelectItem key={s} value={s}>{s.replace(/_/g," ")}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function UsersAdmin() {
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/admin/users").then(r => setUsers(r.data)); }, []);
  return (
    <div>
      <h1 className="text-2xl font-semibold mb-6">Users</h1>
      <div className="bg-white border border-gray-200 rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Admin</TableHead>
              <TableHead>Joined</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.user_id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {u.picture && <img src={u.picture} alt="" className="h-6 w-6 rounded-full" />}
                    {u.name}
                  </div>
                </TableCell>
                <TableCell className="text-xs">{u.email}</TableCell>
                <TableCell>{u.is_admin ? "Yes" : "No"}</TableCell>
                <TableCell className="text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}


function ImageUploader({ mainImage, gallery, onMainChange, onGalleryChange }) {
  const [uploading, setUploading] = useState(false);

  const uploadFiles = async (files, target) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(r.data.url);
      }
      if (target === "main") {
        onMainChange(urls[0]);
      } else {
        onGalleryChange([...(gallery || []), ...urls]);
      }
      toast.success(`Uploaded ${urls.length} image(s)`);
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeFromGallery = (u) => onGalleryChange((gallery || []).filter(g => g !== u));

  return (
    <div className="space-y-3 border border-gray-200 rounded-md p-4">
      <Label className="text-sm font-semibold">Product Images</Label>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-wider text-gray-500">Main image</Label>
          {mainImage ? (
            <div className="mt-2 relative w-32 h-32 rounded-md overflow-hidden bg-gray-50 border">
              <img src={abs(mainImage)} alt="" className="w-full h-full object-contain p-2" />
            </div>
          ) : (
            <div className="mt-2 w-32 h-32 rounded-md border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-xs text-gray-400">No image</div>
          )}
          <label className="mt-2 inline-block cursor-pointer text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded" data-testid="upload-main-image">
            {uploading ? "Uploading..." : "Upload / Replace"}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadFiles(Array.from(e.target.files), "main")} />
          </label>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider text-gray-500">Gallery ({(gallery || []).length})</Label>
          <div className="mt-2 flex flex-wrap gap-2">
            {(gallery || []).map((g, i) => (
              <div key={i} className="relative w-16 h-16 rounded-md overflow-hidden bg-gray-50 border group">
                <img src={abs(g)} alt="" className="w-full h-full object-contain p-1" />
                <button onClick={() => removeFromGallery(g)} className="absolute top-0 right-0 bg-red-500 text-white text-[10px] w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100" data-testid={`remove-gallery-${i}`}>×</button>
              </div>
            ))}
            <label className="w-16 h-16 rounded-md border-2 border-dashed border-gray-300 flex items-center justify-center text-2xl text-gray-400 cursor-pointer hover:border-[#E65A28]" data-testid="upload-gallery-image">
              +
              <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadFiles(Array.from(e.target.files), "gallery")} />
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}

function BenefitHighlightsEditor({ value, onChange }) {
  const [uploading, setUploading] = useState(false);

  const add = () => onChange([...(value || []), { image: "", title: "", text: "" }]);
  const update = (i, key, v) => {
    const next = [...(value || [])];
    next[i] = { ...next[i], [key]: v };
    onChange(next);
  };
  const remove = (i) => onChange((value || []).filter((_, idx) => idx !== i));

  const uploadImage = async (i, file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update(i, "image", r.data.url);
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  return (
    <div className="space-y-3 border border-gray-200 rounded-md p-4">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold">Benefit Highlights (shown on product page)</Label>
        <button onClick={add} type="button" className="text-xs bg-[#0A192F] text-white px-3 py-1.5 rounded" data-testid="add-benefit-highlight">+ Add benefit</button>
      </div>
      {(value || []).map((bh, i) => (
        <div key={i} className="grid grid-cols-1 md:grid-cols-[80px_1fr_1fr_auto] gap-3 items-start border rounded p-3 bg-gray-50">
          <div>
            {bh.image ? <img src={abs(bh.image)} alt="" className="w-16 h-16 object-contain rounded bg-white border" /> : <div className="w-16 h-16 bg-white border-2 border-dashed rounded" />}
            <label className="mt-1 block text-[10px] text-blue-600 cursor-pointer">
              Upload
              <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadImage(i, e.target.files[0])} />
            </label>
          </div>
          <div><Label className="text-xs">Title</Label><Input value={bh.title || ""} onChange={(e) => update(i, "title", e.target.value)} placeholder="e.g. Reduces hair fall" /></div>
          <div><Label className="text-xs">Text</Label><Textarea rows={2} value={bh.text || ""} onChange={(e) => update(i, "text", e.target.value)} placeholder="Short explanation..." /></div>
          <button onClick={() => remove(i)} type="button" className="text-red-600 text-xs mt-6" data-testid={`remove-benefit-${i}`}>Remove</button>
        </div>
      ))}
      {uploading && <p className="text-xs text-gray-500">Uploading...</p>}
    </div>
  );
}
